'use client'

import { OTPInput, OTPInputContext } from 'input-otp'
import * as React from 'react'

import { cn } from '@/lib/utils'

// shadcn input-otp, re-skinned to the in-app spine's tokens (signin
// mini-spec §4.10.1). Exports, props and data-slot attributes are the
// stock ones; only the classes changed — the stock file referenced shadcn
// CSS-variable tokens this project never adopted.

function InputOTP({
    className,
    containerClassName,
    ...props
}: React.ComponentProps<typeof OTPInput> & {
    containerClassName?: string
}) {
    return (
        <OTPInput
            data-slot="input-otp"
            containerClassName={cn(
                'cn-input-otp flex items-center justify-center gap-3 has-disabled:opacity-60',
                containerClassName
            )}
            spellCheck={false}
            className={cn('disabled:cursor-not-allowed', className)}
            {...props}
        />
    )
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="input-otp-group"
            className={cn('flex items-center', className)}
            {...props}
        />
    )
}

function InputOTPSlot({
    index,
    className,
    ...props
}: React.ComponentProps<'div'> & {
    index: number
}) {
    const inputOTPContext = React.useContext(OTPInputContext)
    const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {}

    return (
        <div
            data-slot="input-otp-slot"
            data-active={isActive}
            className={cn(
                // Joined hairline cells on the spine's "carved-in" surface,
                // 44×52 tap targets, serif tabular numerals (spec §4.10.1).
                'relative flex h-13 w-11 items-center justify-center border-y border-r border-rule bg-surface-sunken font-serif text-h2 text-ink tabular-nums first:border-l',
                'aria-invalid:border-error',
                // The one focus treatment every interactive element on the
                // page uses; the library marks the slot under the caret.
                'data-[active=true]:z-10 data-[active=true]:outline-2 data-[active=true]:outline-offset-2 data-[active=true]:outline-focus-ring',
                className
            )}
            {...props}
        >
            {char}
            {hasFakeCaret && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="h-6 w-px animate-caret-blink bg-ink motion-reduce:animate-none" />
                </div>
            )}
        </div>
    )
}

function InputOTPSeparator({ ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="input-otp-separator"
            className="flex items-center"
            role="separator"
            {...props}
        >
            <span aria-hidden className="h-px w-4 bg-rule" />
        </div>
    )
}

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot }
